-- | This module contains helpers for accessing the GraphQL API.
module GraphQL where

import Common

import Affjax as AX
import Affjax.Web as AW
import Affjax.RequestBody as AXR
import Affjax.ResponseFormat as ResponseFormat
import Data.Argonaut (class DecodeJson, class EncodeJson, Json, encodeJson)
import Data.Argonaut as J
import Data.Either (Either(..))
import Data.Foldable (intercalate)
import Data.HTTP.Method (Method(..))
import Data.Maybe (Maybe(..))
import Effect.Aff (Aff)
import Error (ErrorContext(..), ErrorWrapper(..), tagContext, wrapJsonError)
import Foreign.Object (Object)
import Foreign.Object as O
import Web.File.Blob (Blob)
import Web.XHR.FormData (EntryName(..), FileName)
import Web.XHR.FormData as FD

-- NOTE: If you are running into issues with the endpoint make sure you are
-- building with the right flags.
foreign import graphqlEndpoint :: String

type QueryName = String

-- FIXME: We can make the queries a little bit smarter to work with such as
-- filtering our parameters if we aren't including the variables.
query :: forall vars. EncodeJson vars => String -> vars -> Aff (Either ErrorWrapper Json)
query qstr vars = tagContext queryCtx <$> do
    let content = encodeJson { query: qstr, variables: vars }
    res <- AW.request $ AW.defaultRequest {
        method = Left POST,
        url = graphqlEndpoint,
        content = Just $ AXR.Json content,
        responseFormat = ResponseFormat.json,
        withCredentials = true
    }
    handleResult res
  where
    queryCtx = InQuery { query: qstr, vars: encodeJson vars }

-- | Query the API and get the result of the inner "data" field.
queryData
    :: forall vars a. EncodeJson vars
    => DecodeJson a
    => String
    -> vars
    -> Aff (Either ErrorWrapper a)
queryData qstr vars = do
    res <- query qstr vars
    pure (getResultData =<< res)

handleResult :: Either AX.Error (AX.Response Json) -> Aff (Either ErrorWrapper Json)
handleResult result = pure case result of
    Left err -> Left $ AffJaxError err
    Right res -> do
        case decodeJObject res.body of
            Right obj | O.member "errors" obj -> Left $ extractErrors obj
            _ -> Right res.body

-- | Reads JSON response from a failed query and turns it into the appropriate error.
extractErrors :: Object Json -> ErrorWrapper
extractErrors obj = collapse $ wrapJsonError' (J.fromObject obj) do
    msgs :: Array Json <- obj .: "errors"
    Right $ QueryError msgs
  where
    collapse :: Either ErrorWrapper ErrorWrapper -> ErrorWrapper
    collapse (Right err) = err
    collapse (Left err) = WithContext (DuringAction "gathering information about another error") err

-- Get data from within a data obj field.
getResultData :: forall a. DecodeJson a => Json -> Either ErrorWrapper a
getResultData json = wrapJsonError do
    obj <- decodeJObject json
    obj .: "data"

-- | Helps you put together a query with multiple params.
queryStringParams :: forall f. Foldable f => f String -> String
queryStringParams params = "(" <> intercalate "," params <> ")"

-- | Runs a query with an attached file upload.
-- |
-- | The third argument should specify the variable name for the file.
-- |
-- | The fourth argument should specify the filename if applicable.
fileQuery :: forall vars. EncodeJson vars => String -> vars -> String -> Maybe FileName -> Blob
    -> Aff (Either ErrorWrapper Json)
fileQuery qstr vars vname fname blob = do
    let content = encodeJson { query: qstr, variables: vars }
    formData <- liftEffect $ do
        fd <- FD.new
        FD.append (EntryName "operations") (J.stringify content) fd
        FD.append (EntryName "map") (fileUploadMap $ "variables." <> vname) fd
        FD.appendBlob (EntryName "0") blob fname fd
        pure fd

    runMultipartQuery formData

-- | Runs a batched GraphQL request with one attached file upload.
-- |
-- | `filePath` is the GraphQL multipart request path, for example
-- | `0.variables.data.file` for the first operation in a batched request.
batchFileQuery :: forall vars. EncodeJson vars => vars -> String -> Maybe FileName -> Blob
    -> Aff (Either ErrorWrapper Json)
batchFileQuery operations filePath fname blob = do
    let content = encodeJson operations
    formData <- liftEffect $ do
        fd <- FD.new
        FD.append (EntryName "operations") (J.stringify content) fd
        FD.append (EntryName "map") (fileUploadMap filePath) fd
        FD.appendBlob (EntryName "0") blob fname fd
        pure fd

    runMultipartQuery formData

runMultipartQuery :: FD.FormData -> Aff (Either ErrorWrapper Json)
runMultipartQuery formData = do
    res <- AW.request $ AW.defaultRequest {
        method = Left POST,
        url = graphqlEndpoint,
        content = Just $ AXR.FormData formData,
        responseFormat = ResponseFormat.json,
        withCredentials = true
    }
    handleResult res

fileUploadMap :: String -> String
fileUploadMap path = "{\"0\":[\"" <> path <> "\"]}"
